/**
 * A generated image, its download, and the social/web crop exports.
 *
 * Two behaviours here are about telling the user the truth rather than about layout:
 *
 * - **A failed preview says the link expired, not that generation failed.** Signed URLs last
 *   about an hour. Left to the browser's own broken-image glyph, a stale link reads as a
 *   failed generation when the image was created and stored perfectly well — so the failure
 *   state says exactly which of the two happened, and what to do about it.
 * - **Clicking the image sets the focal point every crop protects.** The presets are real
 *   platform placements rather than a general cropping tool, and each crops around a declared
 *   point instead of naively centring. It starts at dead centre, matching the server's own
 *   default, so a crop downloaded before ever clicking still produces a sensible result rather
 *   than an undefined one.
 */

import ImageNotSupportedOutlined from "@mui/icons-material/ImageNotSupportedOutlined";
import { Box, Button, ButtonBase, Chip, Stack, Typography } from "@mui/material";
import { useState } from "react";

import { downloadCropExport, downloadGeneration } from "@/api/downloads";
import type { GenerationResult } from "@/api/queries";
import { useSaveFocalPoint } from "@/api/queries";
import { useAuth } from "@/auth/AuthProvider";
import { CROP_PRESET_OPTIONS } from "@/constants/generate";
import { PRINT_SIZE_LABELS, RATIO_ASPECT_CSS } from "@/constants/imageOutput";
import { GLOW_FRAME, RADIUS_PX, jp } from "@/theme/tokens";

/** Dead centre — the same default the server applies when nothing has been recorded. */
const DEFAULT_FOCAL_POINT = { x: 0.5, y: 0.5 };

export function OutputPreview({ result }: { result: GenerationResult }) {
  const { idToken } = useAuth();
  const saveFocalPoint = useSaveFocalPoint();

  /*
   * The URL that failed, not a boolean — so a new URL is un-failed by comparison rather than
   * by an effect resetting a flag. Without that, an earlier failure would suppress a later
   * success for one render; with an effect, it would be a cascading render for a value that is
   * a pure function of the props. Same shape as the guardrail acknowledgement storing the text
   * it acknowledged.
   */
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const failed = failedUrl === result.previewUrl;

  const [focalPoint, setFocalPoint] = useState(DEFAULT_FOCAL_POINT);
  const [busyPreset, setBusyPreset] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  function commitFocalPoint(point: { x: number; y: number }) {
    setFocalPoint(point);
    // Best-effort: a failed save just means the next export falls back to the last saved
    // point, or to centre. Nothing on this page depends on it, so it has no error surface.
    saveFocalPoint.mutate({ generationId: result.generationId, ...point });
  }

  function handleSetFocalPoint(event: React.MouseEvent<HTMLElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    commitFocalPoint({
      x: (event.clientX - rect.left) / rect.width,
      y: (event.clientY - rect.top) / rect.height,
    });
  }

  /*
   * The keyboard equivalent of clicking the image.
   *
   * Setting the focal point was an `onClick` on an `<img>`, which is not focusable, not in
   * the tab order and announces nothing. Every crop export therefore centre-cropped for
   * anyone not using a pointer, with no way to say otherwise — on the one control whose
   * entire purpose is stopping a crop cutting through the subject.
   *
   * The image is a real button now, and the arrows nudge the point in 5% steps. Stepping
   * rather than typing a coordinate is the right granularity: a crop guide gets adjusted
   * *towards* something, by eye. Clamped rather than wrapped — a point stepping off the right
   * edge and reappearing on the left would be nonsense here.
   */
  function handleFocalPointKeyDown(event: React.KeyboardEvent<HTMLElement>) {
    const STEP = 0.05;
    const deltas: Record<string, { x: number; y: number }> = {
      ArrowLeft: { x: -STEP, y: 0 },
      ArrowRight: { x: STEP, y: 0 },
      ArrowUp: { x: 0, y: -STEP },
      ArrowDown: { x: 0, y: STEP },
    };
    const delta = deltas[event.key];
    if (!delta) return;

    event.preventDefault();
    const clamp = (n: number) => Math.min(1, Math.max(0, n));
    commitFocalPoint({ x: clamp(focalPoint.x + delta.x), y: clamp(focalPoint.y + delta.y) });
  }

  async function handleDownload() {
    setDownloading(true);
    setActionError(null);
    try {
      await downloadGeneration(result.generationId, idToken);
    } catch {
      setActionError("Download failed, please try again.");
    } finally {
      setDownloading(false);
    }
  }

  async function handleDownloadCrop(presetKey: string) {
    setBusyPreset(presetKey);
    setActionError(null);
    try {
      await downloadCropExport(result.generationId, presetKey, idToken);
    } catch {
      setActionError("Export failed, please try again.");
    } finally {
      setBusyPreset(null);
    }
  }

  return (
    <Stack spacing={2}>
      <Box>
        <Chip
          size="small"
          label={
            result.outputType === "print" && result.printSize
              ? `Print · ${PRINT_SIZE_LABELS[result.printSize]} · 300 DPI`
              : "Digital"
          }
        />
      </Box>

      {failed ? (
        <Stack
          role="alert"
          spacing={1}
          sx={{
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            p: 3,
            maxHeight: "70vh",
            aspectRatio: RATIO_ASPECT_CSS[result.ratio],
            border: `1px dashed ${jp.line}`,
            backgroundColor: "rgba(250,240,225,0.6)",
          }}
        >
          <ImageNotSupportedOutlined sx={{ color: "text.secondary" }} />
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Preview couldn&rsquo;t be loaded
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 320 }}>
            The image was generated and saved, only this preview link failed. Links expire
            after about an hour; reload the page for a fresh one, or find it in your history.
          </Typography>
        </Stack>
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <ButtonBase
            onClick={handleSetFocalPoint}
            onKeyDown={handleFocalPointKeyDown}
            // The button *is* the image, so letting the alt text form the name would announce
            // the picture twice over. This says what activating it does, which `alt` cannot.
            aria-label="Set the focal point that crops protect — click a spot, or use the arrow keys"
            sx={{ position: "relative", display: "block", borderRadius: `${RADIUS_PX}px`, cursor: "crosshair" }}
          >
            <Box
              component="img"
              src={result.previewUrl}
              alt={result.altText ?? "Generated image"}
              onError={() => setFailedUrl(result.previewUrl)}
              sx={{
                ...GLOW_FRAME,
                display: "block",
                maxHeight: "70vh",
                width: "auto",
                maxWidth: "100%",
                aspectRatio: RATIO_ASPECT_CSS[result.ratio],
                objectFit: "cover",
              }}
            />
            {/* Ringed in white *and* near-black: a single white ring disappeared against a
                pale sky or a light studio backdrop, which is exactly the kind of image whose
                focal point needs moving off centre. */}
            <Box
              aria-hidden="true"
              sx={{
                position: "absolute",
                pointerEvents: "none",
                height: 16,
                width: 16,
                borderRadius: "50%",
                border: "2px solid #FFFFFF",
                outline: "1px solid rgba(0,0,0,0.5)",
                boxShadow: 1,
                transform: "translate(-50%, -50%)",
                left: `${focalPoint.x * 100}%`,
                top: `${focalPoint.y * 100}%`,
              }}
            />
          </ButtonBase>
        </Box>
      )}

      {/* Wraps, because the note is a full sentence and Download does not shrink — on a phone
          the two stack rather than the text being crushed into a narrow column. */}
      <Stack
        direction="row"
        spacing={2}
        useFlexGap
        sx={{ flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}
      >
        <Typography variant="caption" color={actionError ? "error" : "text.secondary"}>
          {actionError ??
            (result.outputType === "print"
              ? "This preview link expires in about an hour. Downloads as a print-ready JPEG."
              : "This preview link expires in about an hour. Download anything you want to keep.")}
        </Typography>
        <Button variant="outlined" loading={downloading} onClick={() => void handleDownload()}>
          Download
        </Button>
      </Stack>

      <Stack
        spacing={1}
        sx={{ p: 1.5, border: `1px solid ${jp.line}`, backgroundColor: "rgba(255,255,255,0.6)" }}
      >
        <Typography variant="overline" color="text.secondary">
          Export for social &amp; web
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Click the image above — or focus it and use the arrow keys — to set the focal point
          that these crops protect{saveFocalPoint.isPending ? " — saving…" : "."}
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
          {CROP_PRESET_OPTIONS.map((preset) => (
            <Button
              key={preset.key}
              size="small"
              variant="outlined"
              loading={busyPreset === preset.key}
              onClick={() => void handleDownloadCrop(preset.key)}
            >
              {preset.label}
            </Button>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
}
